import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import SuperadminLayout, { DarkCard } from "./SuperadminLayout";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Plus, TrashSimple } from "@phosphor-icons/react";

export default function SuperadminFlags() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newKey, setNewKey] = useState("");
  const [newDesc, setNewDesc] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/superadmin/feature-flags");
      setItems(data.items);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const toggle = async (flag, enabled) => {
    try {
      await api.post("/superadmin/feature-flags", { key: flag.key, enabled, description: flag.description });
      setItems((prev) => prev.map((f) => f.key === flag.key ? { ...f, enabled } : f));
    } catch (e) { toast.error(e?.response?.data?.detail || "Échec"); }
  };

  const create = async () => {
    if (!newKey.trim()) return;
    try {
      await api.post("/superadmin/feature-flags", { key: newKey.trim(), enabled: false, description: newDesc.trim() });
      toast.success(`Flag ${newKey} créé`);
      setNewKey(""); setNewDesc("");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Échec création"); }
  };

  const del = async (k) => {
    if (!confirm(`Supprimer le flag ${k} ?`)) return;
    try {
      await api.delete(`/superadmin/feature-flags/${k}`);
      load();
    } catch (e) { toast.error("Échec"); }
  };

  return (
    <SuperadminLayout title="Feature flags">
      <p className="text-sm text-[#8494A8] mb-4">
        Interrupteurs plateforme — utilisez-les pour activer/désactiver des fonctionnalités sans redéploiement. Consommés par le backend et le frontend via <code className="text-[#B0C97F]">/api/platform/status</code>.
      </p>

      <DarkCard className="p-6 mb-6" data-testid="flags-new">
        <p className="text-xs uppercase tracking-widest text-[#8494A8] mb-3">Créer un nouveau flag</p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <Label className="text-xs text-[#B7C4D6]">Clé (a-z0-9_.-)</Label>
            <Input value={newKey} onChange={(e) => setNewKey(e.target.value)} placeholder="ex: alerts_critical_scores"
              className="mt-1 bg-[#081221] border-white/10 text-white placeholder:text-[#5F758F]" data-testid="flag-new-key" />
          </div>
          <div className="md:col-span-2">
            <Label className="text-xs text-[#B7C4D6]">Description</Label>
            <Input value={newDesc} onChange={(e) => setNewDesc(e.target.value)}
              placeholder="Objectif de ce flag"
              className="mt-1 bg-[#081221] border-white/10 text-white placeholder:text-[#5F758F]" data-testid="flag-new-desc" />
          </div>
        </div>
        <Button onClick={create} className="mt-4 bg-[#E4572E] hover:bg-[#F27551]" data-testid="flag-create">
          <Plus size={14} className="mr-1" /> Créer
        </Button>
      </DarkCard>

      <DarkCard className="overflow-hidden" data-testid="flags-list">
        {loading && <p className="p-6 text-[#8494A8] text-sm">Chargement…</p>}
        {!loading && items.length === 0 && <p className="p-6 text-[#8494A8] text-sm">Aucun flag configuré</p>}
        {items.map((f) => (
          <div key={f.key} className="flex items-center justify-between px-6 py-4 border-b border-white/5 last:border-b-0" data-testid={`flag-row-${f.key}`}>
            <div className="flex-1 min-w-0">
              <code className="text-white font-mono text-sm">{f.key}</code>
              {f.description && <p className="text-xs text-[#8494A8] mt-1">{f.description}</p>}
            </div>
            <div className="flex items-center gap-3">
              <Switch checked={f.enabled} onCheckedChange={(v) => toggle(f, v)} data-testid={`flag-toggle-${f.key}`} />
              <Button variant="ghost" size="sm" onClick={() => del(f.key)} className="text-red-400 hover:bg-red-500/10 hover:text-red-300" data-testid={`flag-delete-${f.key}`}>
                <TrashSimple size={14} />
              </Button>
            </div>
          </div>
        ))}
      </DarkCard>
    </SuperadminLayout>
  );
}
